import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DocumentTextIcon,
  DocumentArrowUpIcon,
  NewspaperIcon,
  UserGroupIcon,
  XMarkIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { CheckIcon as CheckIconSolid } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import LightNavigation from '../../common/LightNavigation';
import { API_URL } from '../../../config/api';
import axios from 'axios';
import { useChecklist } from '../../../context/ChecklistContext';
import useNotifications from '../../../hooks/useNotifications';
import { useAuth } from '../../../hooks/useAuth';

import './../../../styles/PreClosingChecklistRedesigned.css';

interface ChecklistItem {
  id: string;
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  action?: () => void;
  estimatedTime?: string;
  requiredDocs?: string[];
  warningMessage?: string;
}

interface ModalContentProps {
  item: ChecklistItem;
  facilityId: string;
  onClose: () => void;
  onComplete: () => void;
  facilityData: any;
  onRefreshChecklist?: () => void;
}

// Professional color scheme
const colors = {
  primary: '#1e3a5f', // Dark blue from logo
  primaryLight: '#2c4f7c',
  primaryDark: '#0f1f33',
  accent: '#ff6b35', // Orange accent
  accentLight: '#ff8c5a',
  accentDark: '#e55a2b',
  success: '#10b981',
  successLight: '#34d399',
  warning: '#f59e0b',
  error: '#ef4444',
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827'
  }
};

// Enhanced Deal Documents Modal
const DealDocumentsModal: React.FC<ModalContentProps> = ({ item, facilityId, onClose, onComplete, facilityData, onRefreshChecklist }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<string | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const steps = [
    { id: 'review', title: 'Review Documents', icon: <DocumentTextIcon className="w-5 h-5" /> },
    { id: 'verify', title: 'Verify Information', icon: <ShieldCheckIcon className="w-5 h-5" /> },
    { id: 'sign', title: 'Sign Documents', icon: <ClipboardDocumentCheckIcon className="w-5 h-5" /> },
    { id: 'complete', title: 'Complete', icon: <CheckIconSolid className="w-5 h-5" /> }
  ];

  // Polling effect to check signing status when on step 2
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (currentStep === 2 && facilityId) {
      // Start polling every 10 seconds
      interval = setInterval(async () => {
        try {
          setCheckingStatus(true);
          const statusResponse = await axios.get(
            `${API_URL}/api/pre-closing/signing-status/${facilityId}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          if (statusResponse.data.success && statusResponse.data.localStatus === 'completed') {
            console.log('Document signing completed, advancing to step 3');
            setCurrentStep(3);
            clearInterval(interval);
          }
        } catch (error) {
          console.error('Error checking signing status:', error);
        } finally {
          setCheckingStatus(false);
        }
      }, 10000); // Check every 10 seconds
      
      // Also check immediately
      setTimeout(async () => {
        try {
          setCheckingStatus(true);
          const statusResponse = await axios.get(
            `${API_URL}/api/pre-closing/signing-status/${facilityId}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          if (statusResponse.data.success && statusResponse.data.localStatus === 'completed') {
            console.log('Document already signed, advancing to step 3');
            setCurrentStep(3);
          }
        } catch (error) {
          console.error('Error checking initial signing status:', error);
        } finally {
          setCheckingStatus(false);
        }
      }, 2000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentStep, facilityId]);

  const handlePreviewDocument = async (documentType: string) => {
    if (!facilityId) {
      alert('Facility data is still loading. Please try again in a moment.');
      return;
    }

    try {
      // TEMP: Until dedicated templates are finalized, route these previews to the Master Facility Agreement preview.
      const resolvedDocumentType =
        documentType === 'registration-rights-agreement' || documentType === 'officer-certificate'
          ? 'master-facility-agreement'
          : documentType;

      // Live env can force downloads via Content-Disposition: attachment.
      // Fetch as a blob and preview using a blob URL to guarantee inline rendering in the iframe.
      const pdfUrl = `${API_URL}/api/signature/view-pdf-direct/${facilityId}/${resolvedDocumentType}?token=${localStorage.getItem('token')}`;
      console.log('Opening PDF preview (blob):', pdfUrl);

      // cleanup previous blob url (if any)
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
        setPreviewBlobUrl(null);
      }

      const resp = await axios.get(pdfUrl, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const contentType = resp.headers?.['content-type'] || resp.data?.type;
      if (contentType && !String(contentType).includes('application/pdf')) {
        // If backend returns a non-PDF (eg docx fallback), let the browser download it instead of showing a blank iframe.
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const blobUrl = URL.createObjectURL(resp.data);
      setPreviewBlobUrl(blobUrl);
      setPreviewDocument(blobUrl);
      
    } catch (error) {
      console.error('Error fetching document preview:', error);
      alert('Unable to preview document. The document may not be generated yet.');
    }
  };

  const handleInitiateSignature = async () => {
    if (!facilityId) {
      alert('Facility data is still loading. Please try again in a moment.');
      return;
    }

    setLoading(true);
    try {
      const mergeResponse = await axios.post(
        `${API_URL}/api/pre-closing/merge-and-prepare/${facilityId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (mergeResponse.data.success) {
        const signingResponse = await axios.get(
          `${API_URL}/api/pre-closing/signnow/embed-link/${facilityId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (signingResponse.data.success && signingResponse.data.embedLink) {
          setSigningUrl(signingResponse.data.embedLink);
          setCurrentStep(2);
        }
      }
    } catch (error) {
      console.error('Error initiating signature:', error);
      alert('Failed to prepare documents for signing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-3" style={{ color: colors.primaryDark }}>
          {item.title}
        </h2>
        <p className="text-gray-600 text-lg">{item.description}</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-6">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${
                  index <= currentStep 
                    ? 'border-orange-500 bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-lg' 
                    : 'border-gray-300 bg-white text-gray-400'
                }`}
                style={{
                  borderColor: index <= currentStep ? colors.accent : colors.gray[300],
                  background: index <= currentStep 
                    ? `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
                    : 'white'
                }}
              >
                {index < currentStep ? (
                  <CheckIconSolid className="w-6 h-6 text-white" />
                ) : (
                  <div className={index <= currentStep ? 'text-white' : 'text-gray-400'}>
                    {step.icon}
                  </div>
                )}
              </motion.div>
              {index < steps.length - 1 && (
                <div className="flex-1 mx-3">
                  <div className="h-1 rounded-full bg-gray-200 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      initial={{ width: 0 }}
                      animate={{ 
                        width: index < currentStep ? '100%' : '0%',
                      }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      style={{
                        background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight})`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between">
          {steps.map((step) => (
            <div key={step.id} className="text-xs text-center font-medium" style={{ color: colors.gray[600] }}>
              {step.title}
            </div>
          ))}
        </div>
      </div>

      {/* Content based on current step */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-8 shadow-inner">
        {currentStep === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="font-bold text-xl mb-6" style={{ color: colors.primaryDark }}>
              Documents to Review:
            </h3>
            <div className="space-y-4">
              {[
                { name: 'Master Facility Agreement', type: 'master-facility-agreement', description: 'Equity Line of Credit Terms' },
                { name: 'Registration Rights Agreement', type: 'registration-rights-agreement', description: 'SEC Registration Requirements' },
                { name: 'Secretary\'s Certificate', type: 'officer-certificate', description: 'Corporate Authorization' }
              ].map((doc, index) => (
                <motion.div
                  key={doc.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-orange-400 transition-all duration-300 cursor-pointer group"
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform"
                    style={{ 
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})` 
                    }}
                  >
                    <DocumentTextIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: colors.primaryDark }}>{doc.name}</p>
                    <p className="text-sm text-gray-500">{doc.description}</p>
                  </div>
                  <button 
                    className={`font-medium flex items-center transition-colors ${
                      !facilityId ? 'text-gray-400 cursor-not-allowed' : 'text-orange-500 hover:text-orange-600'
                    }`}
                    style={{ color: !facilityId ? undefined : colors.accent }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewDocument(doc.type);
                    }}
                    disabled={!facilityId}
                  >
                    {!facilityId ? 'Loading...' : 'Download'}
                    <ChevronRightIcon className="w-4 h-4 ml-1" />
                  </button>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Please review all documents carefully. These are legally binding agreements that will govern your equity facility.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="font-bold text-xl mb-6" style={{ color: colors.primaryDark }}>
              Verify Deal Information:
            </h3>
            <div className="grid grid-cols-2 gap-6">
              {[
                { label: 'Company Name', value: facilityData?.companyName || 'N/A' },
                { label: 'Ticker Symbol', value: facilityData?.ticker || 'N/A' },
                { label: 'Facility Amount', value: `$${(facilityData?.totalCommitment || 0).toLocaleString()}` },
                { label: 'Term Length', value: `${facilityData?.exercisePeriod || '24'} months` }
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-4 rounded-xl border-2 border-gray-200"
                >
                  <label className="text-sm font-semibold text-gray-600 block mb-2">
                    {item.label}
                  </label>
                  <p className="text-lg font-bold" style={{ color: colors.primaryDark }}>
                    {item.value}
                  </p>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <div className="flex items-start">
                <InformationCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Please verify all information is correct before proceeding to signature. Contact support if any corrections are needed.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {currentStep === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {signingUrl ? (
              <div>
                <div className="h-[600px] bg-white rounded-xl overflow-hidden shadow-2xl mb-4">
                  <iframe
                    src={signingUrl}
                    className="w-full h-full"
                    title="Document Signing"
                  />
                </div>
                
                {/* Status indicator */}
                <div className="text-center p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                  <div className="flex items-center justify-center mb-2">
                    {checkingStatus ? (
                      <ArrowPathIcon className="w-5 h-5 text-blue-600 mr-2 animate-spin" />
                    ) : (
                      <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                    )}
                    <p className="text-sm text-blue-800 font-medium">
                      {checkingStatus ? 'Checking signing status...' : 'Waiting for document completion'}
                    </p>
                  </div>
                  <p className="text-xs text-blue-700 mb-3">
                    Complete your signature in the document above. This page will automatically advance when signing is finished.
                  </p>
                  
                  {/* Manual completion button for testing */}
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="px-4 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Manual: Mark as Complete
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <ArrowPathIcon className="w-16 h-16 text-orange-500 mx-auto mb-4 animate-spin" />
                <p className="text-gray-600 text-lg">Preparing documents for signature...</p>
              </div>
            )}
          </motion.div>
        )}

        {currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center py-12"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${colors.success}, ${colors.successLight})` 
              }}
            >
              <CheckIconSolid className="w-12 h-12 text-white" />
            </motion.div>
            <h3 className="text-2xl font-bold mb-3" style={{ color: colors.primaryDark }}>
              Documents Signed Successfully!
            </h3>
            <p className="text-gray-600 mb-2">
              Your deal documents have been signed and stored securely.
            </p>
            <p className="text-sm text-gray-500">
              You will receive a copy via email shortly.
            </p>
          </motion.div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onClose}
          className="px-6 py-3 text-gray-700 hover:text-gray-900 font-medium transition-colors"
        >
          Cancel
        </button>
        <div className="space-x-4">
          {currentStep > 0 && currentStep < 3 && (
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 font-medium transition-all"
            >
              Back
            </button>
          )}
          {currentStep === 0 && (
            <button
              onClick={() => setCurrentStep(1)}
              className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
              }}
            >
              Continue to Verification
            </button>
          )}
          {currentStep === 1 && (
            <button
              onClick={handleInitiateSignature}
              disabled={loading}
              className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50"
              style={{
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
              }}
            >
              {loading ? 'Preparing...' : 'Proceed to Sign'}
            </button>
          )}
          {currentStep === 3 && (
            <button
              onClick={onComplete}
              className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.success}, ${colors.successLight})`
              }}
            >
              Complete
            </button>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      <AnimatePresence>
        {previewDocument && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setPreviewDocument(null);
              if (previewBlobUrl) {
                URL.revokeObjectURL(previewBlobUrl);
                setPreviewBlobUrl(null);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-xl font-semibold" style={{ color: colors.primaryDark }}>
                  Document Preview
                </h3>
                <button
                  onClick={() => {
                    setPreviewDocument(null);
                    if (previewBlobUrl) {
                      URL.revokeObjectURL(previewBlobUrl);
                      setPreviewBlobUrl(null);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="h-[80vh]">
                <iframe
                  src={previewDocument}
                  className="w-full h-full"
                  title="Document Preview"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// S1 Generation Modal with AI-powered document generation
const S1GenerationModal: React.FC<ModalContentProps> = ({ item, facilityId, onClose, onComplete, facilityData, onRefreshChecklist }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<'idle' | 'generating' | 'completed' | 'error'>('idle');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [pythonTaskId, setPythonTaskId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState<string>('queued');
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [useParallel] = useState(true); // Always use parallel mode
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [generatedDocument, setGeneratedDocument] = useState<any>(null);
  const [downloadLinks, setDownloadLinks] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [recentlyDeleted, setRecentlyDeleted] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  
  // WebSocket integration for real-time updates
  const { user } = useAuth();
  const { notifications, isConnected, forceReconnect } = useNotifications(user?.id);
  
  console.log('🔌 S1GenerationModal - WebSocket status:', {
    isConnected,
    userId: user?.id,
    notificationCount: notifications.length,
    facilityId
  });
  
  // Handle real-time WebSocket notifications
  const handleS1Notification = useCallback((notification: any) => {
    // Skip processing notifications if we recently deleted documents
    if (recentlyDeleted) {
      console.log('🚫 Ignoring notification due to recent deletion:', notification.type);
      return;
    }
    
    const { type, data } = notification;
    
    console.log('🔔 S1GenerationModal received notification:', { type, data });
    
    switch (type) {
      case 's1_generation_started':
      case 's1_generation_regenerated':
        console.log('🚀 S1 generation started notification');
        setRequestId(data.requestId);
        setGenerationStatus('generating');
        setCurrentStep(1);
        break;
        
      case 's1_generation_progress':
        console.log('📈 S1 progress update:', { 
          percentage: data.percentage, 
          stage: data.stage, 
          message: data.message 
        });
        setProgress(data.percentage || 0);
        setProgressStage(data.stage || 'queued');
        setProgressMessage(data.message || '');
        break;
        
      case 's1_generation_completed':
        console.log('✅ S1 generation completed notification');
        setGenerationStatus('completed');
        setProgress(100);
        setProgressStage('completed');
        setCurrentStep(2);
        // Fetch download links
        if (data.requestId) {
          fetchCompletedGeneration(data.requestId);
        }
        break;
        
      case 's1_generation_error':
        console.log('❌ S1 generation error notification');
        setGenerationStatus('error');
        setErrorMessage(data.message || 'Generation failed');
        break;
    }
  }, [recentlyDeleted]);
  
  // Process WebSocket notifications
  useEffect(() => {
    // Skip processing notifications if we recently deleted documents
    if (recentlyDeleted) {
      console.log('🚫 Skipping notification processing due to recent deletion');
      return;
    }
    
    const s1Notifications = notifications.filter((n: any) => 
      n.type.startsWith('s1_generation')
    );
    
    console.log('🔍 S1GenerationModal - Filtered S1 notifications:', s1Notifications.length);
    
    // Process the most recent unread notification
    const latestNotification = s1Notifications.find((n: any) => !n.read);
    if (latestNotification) {
      console.log('📨 Processing latest S1 notification:', latestNotification);
      handleS1Notification(latestNotification);
    }
  }, [notifications, handleS1Notification, recentlyDeleted]);
  
  // Fetch completed generation details
  const fetchCompletedGeneration = async (requestId: string) => {
    try {
      console.log('📥 Fetching completed generation details for:', requestId);
      const response = await axios.get(
        `${API_URL}/api/s1-generation/facilities/${facilityId}/status`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success && response.data.downloadLinks) {
        console.log('📁 Setting download links:', response.data.downloadLinks);
        setDownloadLinks(response.data.downloadLinks);
      }
    } catch (error) {
      console.error('Error fetching completed generation:', error);
    }
  };
  
  const steps = [
    { id: 'prepare', title: 'Prepare Data', icon: <DocumentTextIcon className="w-5 h-5" /> },
    { id: 'generate', title: 'Generate S-1', icon: <SparklesIcon className="w-5 h-5" /> },
    { id: 'review', title: 'Download Files', icon: <ShieldCheckIcon className="w-5 h-5" /> },
    { id: 'complete', title: 'Complete', icon: <CheckIconSolid className="w-5 h-5" /> }
  ];

  

  // Reset all state to initial values
  const resetToInitialState = useCallback(() => {
    console.log('🔄 Resetting S1GenerationModal to initial state');
    setGenerationStatus('idle');
    setCurrentStep(0);
    setRequestId(null);
    setPythonTaskId(null);
    setProgress(0);
    setProgressStage('queued');
    setProgressMessage('');
    setErrorMessage(null);
    setGeneratedDocument(null);
    setDownloadLinks(null);
    setIsDeleting(false);
    setIsRegenerating(false);
  }, []);

  // Check for existing generation on component mount (but not if recently deleted)
  useEffect(() => {
    if (!recentlyDeleted) {
      console.log('🔄 S1GenerationModal mounted, checking existing generation...');
      checkExistingGeneration();
    } else {
      console.log('🚫 Skipping checkExistingGeneration due to recent deletion');
      // Reset to initial state when recently deleted
      resetToInitialState();
    }
  }, [facilityId, recentlyDeleted, resetToInitialState]);

  const checkExistingGeneration = async () => {
    try {
      console.log('🔍 Checking existing S1 generation for facility:', facilityId);
      const response = await axios.get(
        `${API_URL}/api/s1-generation/facilities/${facilityId}/status`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('📊 S1 Generation status response:', response.data);

      if (response.data.success && response.data.status) {
        const { status } = response.data;
        console.log('📋 Generation status:', status);
        
        if (['pending', 'running'].includes(status)) {
          // Active generation - start on generation step with polling
          setRequestId(response.data.requestId);
          setPythonTaskId(response.data.pythonTaskId);
          setGenerationStatus('generating');
          setProgress(response.data.progressPercentage || 0);
          setProgressStage(response.data.progressStage || 'queued');
          setProgressMessage(response.data.progressMessage || '');
          setCurrentStep(1);
          pollStatus(response.data.requestId);
        } else if (status === 'completed') {
          // Completed generation - show in Prepare Data step for user choice
          console.log('✅ Found completed S1 generation:', response.data.requestId);
          setRequestId(response.data.requestId);
          setPythonTaskId(response.data.pythonTaskId);
          setGenerationStatus('completed');
          setProgress(100);
          setProgressStage('completed');
          setProgressMessage(response.data.progressMessage || 'S1 document generated successfully');
          
          // Set download links if available, or create them from requestId
          if (response.data.downloadLinks) {
            console.log('📁 Using provided download links:', response.data.downloadLinks);
            setDownloadLinks(response.data.downloadLinks);
          } else if (response.data.requestId) {
            // Create download links even if backend didn't provide them
            const baseUrl = `/api/s1-generation/${response.data.requestId}/download`;
            const generatedLinks = {
              docx: `${baseUrl}/docx`,
              markdown: `${baseUrl}/markdown`,
              report: `${baseUrl}/report`
            };
            console.log('📁 Generated download links:', generatedLinks);
            setDownloadLinks(generatedLinks);
          }
          
          setGeneratedDocument(response.data);
          console.log('📋 Staying on step 0 to show existing documents');
          // Stay on step 0 (Prepare Data) to show existing documents
          // Explicitly ensure we don't jump to a different step
          setCurrentStep(0);
        }
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('✅ No existing S1 generation found - starting fresh');
        // This is expected when no valid generation exists
        resetToInitialState();
      } else {
        console.warn('Error checking existing generation:', error.response?.data || error.message);
      }
    }
  };

  const handleStartGeneration = async () => {
    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }

    if (currentStep !== 1 || loading) return;

    setLoading(true);
    setGenerationStatus('generating');
    setProgress(0);
    setProgressStage('queued');

    try {
      // Check WebSocket connection before starting
      if (!isConnected) {
        console.log('⚠️ S1GenerationModal: WebSocket not connected, attempting to reconnect...');
        forceReconnect();
      } else {
        console.log('✅ S1GenerationModal: WebSocket connected, real-time updates will work');
      }
      
      // Start S1 generation using Python FastAPI
      const response = await axios.post(
        `${API_URL}/api/s1-generation/facilities/${facilityId}/generate`,
        {
          useParallel: useParallel,
          companyName: facilityData?.companyName || facilityData?.name
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setRequestId(response.data.requestId);
        setPythonTaskId(response.data.pythonTaskId);
        pollStatus(response.data.requestId);
      } else {
        throw new Error(response.data.message || 'Failed to start generation');
      }
    } catch (error: any) {
      console.error('Error starting S1 generation:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to start S1 generation');
      setGenerationStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const pollStatus = async (reqId: string) => {
    const maxAttempts = 600; // 100 minutes max for Python API (parallel: 20min, sequential: 90min)
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/s1-generation/${reqId}/progress`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (response.data.success) {
          const { status, progressPercentage, progressStage: stage, progressMessage: message } = response.data;
          
          setProgress(progressPercentage || 0);
          setProgressStage(stage || 'queued');
          setProgressMessage(message || '');

          console.log(`S1 Generation: ${stage} (${progressPercentage || 0}%) - ${message}`);

          if (status === 'completed') {
            setGenerationStatus('completed');
            
            // Fetch full status with download links
            const fullResponse = await axios.get(
              `${API_URL}/api/s1-generation/${reqId}/status`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            if (fullResponse.data.success && fullResponse.data.downloadLinks) {
              setDownloadLinks(fullResponse.data.downloadLinks);
              setGeneratedDocument(fullResponse.data);
            }
            
            // Mark the checklist item as completed
            localStorage.setItem(`checklist_s1-filing_${facilityId}`, 'completed');
            
            // Only advance to step 2 if we're not recently deleted
            if (!recentlyDeleted) {
              setCurrentStep(2);
            }
            return;
          } else if (status === 'failed') {
            setErrorMessage(response.data.errorDetails || 'Generation failed');
            setGenerationStatus('error');
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          // Longer polling intervals during pipeline stage
          const delay = progressStage === 'pipeline' ? 15000 : 10000;
          setTimeout(poll, delay);
        } else {
          setErrorMessage('Generation timed out. Please try again.');
          setGenerationStatus('error');
        }
      } catch (error: any) {
        console.error('Error polling status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 20000); // Retry after longer delay on error
        } else {
          setErrorMessage(error.message || 'Failed to check status');
          setGenerationStatus('error');
        }
      }
    };

    poll();
  };

  // Legacy function - no longer used with Python API
  // const generateLegalReview = async (s1Data: any) => {
  //   // This function is obsolete with Python API integration
  // };

  const handleDownloadFile = async (fileType: 'docx' | 'pdf' | 'markdown' | 'report') => {
    const effectiveRequestId = requestId || generatedDocument?.requestId || generatedDocument?._id;
    if (!effectiveRequestId) {
      alert('No document request ID available. Please try regenerating the documents.');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/s1-generation/${effectiveRequestId}/download/${fileType}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob'
        }
      );

      // Create blob and download
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const extensions = { docx: 'docx', pdf: 'pdf', markdown: 'md', report: 'json' };
      const extension = extensions[fileType];
      
      a.download = `S1_${facilityData?.ticker || 'Document'}_${timestamp}.${extension}`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleDeleteDocuments = async () => {
    if (!window.confirm('This will permanently delete your existing S1 documents. Are you sure you want to continue?')) {
      return;
    }

    if (!requestId) {
      alert('No document request ID found.');
      return;
    }

    setIsDeleting(true);
    try {
      console.log('🗑️ Starting document deletion...');
      
      const response = await axios.delete(
        `${API_URL}/api/s1-generation/${requestId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete S1 documents');
      }
      
      console.log('✅ Documents deleted successfully');
      
      // Reset states to reflect deletion using the reset function
      resetToInitialState();
      setRecentlyDeleted(true); // Prevent checkExistingGeneration from running
      setPollingActive(false); // Stop any active polling
      
      // Clear the checklist completion status from localStorage
      // Try both possible key patterns to ensure it's cleared
      localStorage.removeItem(`checklist_s1-filing_${facilityId}`);
      
      // Also clear with user.facilityId pattern (in case of mismatch)
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user.facilityId && user.facilityId !== facilityId) {
            localStorage.removeItem(`checklist_s1-filing_${user.facilityId}`);
            console.log('🧹 Also cleared with user.facilityId pattern:', user.facilityId);
          }
        } catch (error) {
          console.warn('Failed to parse user data:', error);
        }
      }
      
      console.log('🧹 Cleared checklist completion status from localStorage');
      
      // Force refresh of checklist items by calling parent callback
      if (onRefreshChecklist) {
        onRefreshChecklist();
        console.log('🔄 Triggered checklist refresh via parent callback');
      }
      
      // Clear the recently deleted flag after a delay
      setTimeout(() => {
        setRecentlyDeleted(false);
      }, 2000);
      
      alert('S1 documents have been deleted successfully.');
      
    } catch (error: any) {
      console.error('Delete error:', error);
      
      let errorMessage = 'Failed to delete S1 documents';
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Documents not found or already deleted.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied. You can only delete your own documents.';
        } else {
          errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrorMessage(errorMessage);
      alert(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAndRegenerate = async () => {
    if (!window.confirm('This will permanently delete your existing S1 documents and generate new ones. Are you sure you want to continue?')) {
      return;
    }

    if (!facilityId) {
      alert('Facility data is required to delete and regenerate documents.');
      return;
    }

    setIsRegenerating(true);
    try {
      console.log('🗑️ Starting delete and regenerate process...');
      
      const response = await axios.post(
        `${API_URL}/api/s1-generation/facilities/${facilityId}/remove-and-regenerate`,
        {
          useParallel: useParallel,
          companyName: facilityData?.companyName
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          timeout: 60000 // 1 minute timeout for initiation
        }
      );
      
      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to remove and regenerate S-1 documents');
      }
      
      console.log('✅ Delete and regenerate initiated:', data);
      
      // Reset states and start new generation
      setRequestId(data.requestId);
      setPythonTaskId(data.pythonTaskId);
      setGenerationStatus('generating');
      setProgress(5);
      setProgressStage('queued');
      setProgressMessage('Task queued for processing (regeneration)');
      setErrorMessage(null);
      setGeneratedDocument(null);
      setDownloadLinks(null);
      setCurrentStep(1); // Move to generation step
      setRecentlyDeleted(false); // Clear any deletion flag since we're starting fresh
      
      // Start polling for the new generation
      pollStatus(data.requestId);
      
    } catch (error: any) {
      console.error('Delete and regenerate error:', error);
      
      let errorMessage = 'Failed to remove existing documents and start regeneration';
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'S1 generation endpoint not found. Please contact support.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please login again.';
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.message || 'Invalid request parameters';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrorMessage(errorMessage);
      alert(errorMessage);
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-3" style={{ color: colors.primaryDark }}>
          {item.title}
        </h2>
        <p className="text-gray-600 text-lg">{item.description}</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-6">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${
                  index <= currentStep 
                    ? 'border-orange-500 bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-lg' 
                    : 'border-gray-300 bg-white text-gray-400'
                }`}
                style={{
                  borderColor: index <= currentStep ? colors.accent : colors.gray[300],
                  background: index <= currentStep 
                    ? `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
                    : 'white'
                }}
              >
                {index < currentStep ? (
                  <CheckIconSolid className="w-6 h-6 text-white" />
                ) : (
                  <div className={index <= currentStep ? 'text-white' : 'text-gray-400'}>
                    {step.icon}
                  </div>
                )}
                {index === 1 && loading && (
                  <ArrowPathIcon className="w-6 h-6 text-white animate-spin" />
                )}
              </motion.div>
              {index < steps.length - 1 && (
                <div className="flex-1 mx-3">
                  <div className="h-1 rounded-full bg-gray-200 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      initial={{ width: 0 }}
                      animate={{ 
                        width: index < currentStep ? '100%' : '0%',
                      }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      style={{
                        background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight})`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between">
          {steps.map((step) => (
            <div key={step.id} className="text-xs text-center font-medium" style={{ color: colors.gray[600] }}>
              {step.title}
            </div>
          ))}
        </div>
      </div>

      {/* Content based on current step */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-8 shadow-inner">
        {currentStep === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="font-bold text-xl mb-6" style={{ color: colors.primaryDark }}>
              Facility Information:
            </h3>
            <div className="grid grid-cols-2 gap-6">
              {[
                { label: 'Company Name', value: facilityData?.companyName || 'N/A' },
                { label: 'Ticker Symbol', value: facilityData?.ticker || 'N/A' },
                // Dashboard API returns `totalCommitment`; keep `totalOffering` as a fallback for older data shapes
                { label: 'Facility Amount', value: `$${((facilityData?.totalCommitment ?? facilityData?.totalOffering) || 0).toLocaleString()}` },
                { label: 'Term Length', value: `${facilityData?.exercisePeriod || '24'} months` }
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-4 rounded-xl border-2 border-gray-200"
                >
                  <label className="text-sm font-semibold text-gray-600 block mb-2">
                    {item.label}
                  </label>
                  <p className="text-lg font-bold" style={{ color: colors.primaryDark }}>
                    {item.value}
                  </p>
                </motion.div>
              ))}
            </div>
            
            {/* Existing Completed Documents */}
            {generationStatus === 'completed' && (downloadLinks || requestId) && (
              <div className="mt-6 p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <CheckIconSolid className="w-6 h-6 text-green-600 mr-3" />
                    <div>
                      <p className="text-sm text-green-800 font-semibold">S-1 Documents Available</p>
                      <p className="text-xs text-green-700">
                        Generated {generatedDocument?.completedAt && new Date(generatedDocument.completedAt).toLocaleDateString()} • Ready for download
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleDeleteDocuments}
                      disabled={isDeleting || isRegenerating}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs font-medium rounded-full flex items-center gap-1.5 transition-colors"
                      title="Delete existing documents permanently"
                    >
                      <TrashIcon className={`w-3 h-3 ${isDeleting ? 'animate-pulse' : ''}`} />
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      onClick={handleDeleteAndRegenerate}
                      disabled={isDeleting || isRegenerating}
                      className="px-3 py-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs font-medium rounded-full flex items-center gap-1.5 transition-colors"
                      title="Delete existing documents and regenerate new ones"
                    >
                      <ArrowPathIcon className={`w-3 h-3 ${isRegenerating ? 'animate-spin' : ''}`} />
                      {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                    </button>
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      ✓ Complete
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  <button
                    onClick={() => handleDownloadFile('docx')}
                    disabled={!(requestId || generatedDocument?.requestId || generatedDocument?._id)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    DOCX
                  </button>
                  {/* <button
                    onClick={() => handleDownloadFile('pdf')}
                    disabled={!downloadLinks?.pdf}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    PDF
                  </button> */}
                  <button
                    onClick={() => handleDownloadFile('markdown')}
                    disabled={!(requestId || generatedDocument?.requestId || generatedDocument?._id)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    MD
                  </button>
                  {/* <button
                    onClick={() => handleDownloadFile('report')}
                    disabled={!(requestId || generatedDocument?.requestId || generatedDocument?._id)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <InformationCircleIcon className="w-4 h-4" />
                    Report
                  </button> */}
                </div>
                
                <div className="text-center">
                  <p className="text-xs text-green-600">
                    📄 All 42 facility data fields included • Task: {pythonTaskId?.slice(0, 8)}...
                  </p>
                </div>
              </div>
            )}
            
            {/* Divider */}
            {generationStatus === 'completed' && (
              <div className="flex items-center gap-4 mt-6">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="text-sm text-gray-500 font-medium">Generate New Documents</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>
            )}
            
            {/* Generation Mode Selection */}
            <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <div className="flex items-start mb-4">
                <SparklesIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 font-semibold mb-2">Python FastAPI S-1 Generation</p>
                  <p className="text-xs text-blue-700">
                    Fast parallel generation with optimal performance and timing.
                  </p>
                </div>
              </div>
              
              <div className="p-3 rounded-lg border-2 border-blue-500 bg-blue-100">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded-full bg-blue-600" />
                  <span className="font-medium text-sm text-blue-900">
                    Parallel Mode (Optimized)
                  </span>
                </div>
                <p className="text-xs text-blue-700">AI-powered generation</p>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-gray-50 border-2 border-gray-200 rounded-xl">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-800 font-semibold mb-2">Enhanced Features</p>
                  {/* <p className="text-xs text-blue-700">
                    Our advanced system generates S-1 documents section-by-section using Claude Haiku 3.5, 
                    reducing costs by 95% while maintaining professional quality and SEC compliance.
                  </p> */}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center">
              <SparklesIcon className="w-16 h-16 mx-auto mb-4" style={{ color: colors.accent }} />
              <h3 className="text-2xl font-bold mb-3" style={{ color: colors.primaryDark }}>
                Generating S-1 Registration Statement
              </h3>
              <div className="mb-6">
                <p className="text-gray-600 mb-2">
                  Python FastAPI is processing your S1 document using {useParallel ? 'parallel' : 'sequential'} generation...
                </p>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className={`text-xs ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
                    {isConnected ? 'Real-time updates active' : 'Updates via polling only'}
                  </span>
                </div>
                {pythonTaskId && (
                  <p className="text-xs text-gray-500 font-mono">
                    Task ID: {pythonTaskId.slice(0, 8)}...
                  </p>
                )}
                {/* <p className="text-sm font-medium" style={{ color: colors.primary }}>
                  Stage: {progressStage.charAt(0).toUpperCase() + progressStage.slice(1)}
                </p> */}
                {progressMessage && (
                  <p className="text-sm text-gray-600 italic">
                    {progressMessage}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Estimated time: {useParallel ? '20-25 minutes' : '3-5 minutes'} 
                </p>
              </div>
              
              {progress > 0 && (
                <div className="mb-6">
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5 }}
                      style={{
                        background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight})`
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{Math.round(progress)}% complete</p>
                </div>
              )}
              
              {generationStatus === 'error' && (
                <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {currentStep === 2 && generationStatus === 'completed' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="font-bold text-xl mb-6" style={{ color: colors.primaryDark }}>
              S-1 Generation Complete
            </h3>
            <p className="text-gray-600 mb-6">
              Your S-1 registration statement has been generated successfully using Python FastAPI. 
              Multiple file formats are available for download.
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-5 h-5 text-blue-600 mr-2" />
                    <div>
                      <p className="font-medium text-sm">DOCX</p>
                      <p className="text-xs text-gray-500">Word Document</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadFile('docx')}
                    disabled={!(requestId || generatedDocument?.requestId || generatedDocument?._id)}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                  >
                    Download
                  </button>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-5 h-5 text-red-600 mr-2" />
                    <div>
                      <p className="font-medium text-sm">PDF</p>
                      <p className="text-xs text-gray-500">PDF Document</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadFile('pdf')}
                    disabled={!downloadLinks?.pdf}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                  >
                    Download
                  </button>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-5 h-5 text-purple-600 mr-2" />
                    <div>
                      <p className="font-medium text-sm">Markdown</p>
                      <p className="text-xs text-gray-500">MD File</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadFile('markdown')}
                    disabled={!(requestId || generatedDocument?.requestId || generatedDocument?._id)}
                    className="px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                  >
                    Download
                  </button>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-5 h-5 text-orange-600 mr-2" />
                    <div>
                      <p className="font-medium text-sm">Report</p>
                      <p className="text-xs text-gray-500">JSON Report</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadFile('report')}
                    disabled={!(requestId || generatedDocument?.requestId || generatedDocument?._id)}
                    className="px-3 py-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>

            {downloadLinks && (
              <div className="text-center mt-4">
                <p className="text-xs text-gray-500 mb-2">
                  Files generated using {useParallel ? 'parallel' : 'sequential'} mode
                </p>
                {pythonTaskId && (
                  <p className="text-xs text-gray-400 font-mono">
                    Task: {pythonTaskId}
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}

        {currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center py-12"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${colors.success}, ${colors.successLight})` 
              }}
            >
              <CheckIconSolid className="w-12 h-12 text-white" />
            </motion.div>
            <h3 className="text-2xl font-bold mb-3" style={{ color: colors.primaryDark }}>
              S-1 Generation Complete!
            </h3>
            <p className="text-gray-600 mb-2">
              Your registration statement and legal review have been generated successfully.
            </p>
            <p className="text-sm text-gray-500">
              You can download the documents anytime from your dashboard.
            </p>
          </motion.div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onClose}
          className="px-6 py-3 text-gray-700 hover:text-gray-900 font-medium transition-colors"
        >
          {currentStep === 3 ? 'Close' : 'Cancel'}
        </button>
        <div className="space-x-4">
          {currentStep > 0 && currentStep < 3 && (
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 font-medium transition-all"
            >
              Back
            </button>
          )}
          {currentStep === 0 && (
            <button
              onClick={handleStartGeneration}
              disabled={!facilityData} // <<< FIX: Disable button until data is loaded
              className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
              }}
            >
              {!facilityData ? 'Loading Data...' : 'Start Generation'} 
            </button>
          )}
          {currentStep === 1 && generationStatus === 'idle' && (
            <button
              onClick={handleStartGeneration}
              disabled={loading}
              className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50"
              style={{
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
              }}
            >
              {loading ? 'Generating...' : 'Generate S-1'}
            </button>
          )}
          {currentStep === 2 && (
            <button
              onClick={() => setCurrentStep(3)}
              className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
              }}
            >
              Continue
            </button>
          )}
          {currentStep === 3 && (
            <button
              onClick={onComplete}
              className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.success}, ${colors.successLight})`
              }}
            >
              Complete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// 8K Generation Modal with AI-powered document generation
const EightKGenerationModal: React.FC<ModalContentProps> = ({ item, facilityId, onClose, onComplete, facilityData, onRefreshChecklist }) => {
  const { user } = useAuth();
  const { notifications } = useNotifications(user?.id);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<'idle' | 'generating' | 'completed' | 'error'>('idle');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [pythonTaskId, setPythonTaskId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState<string>('queued');
  const [progressMessage, setProgressMessage] = useState<string>('Task queued for processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [files, setFiles] = useState<any>(null);
  const [downloadLinks, setDownloadLinks] = useState<any>(null);
  const [existingGeneration, setExistingGeneration] = useState<any>(null);
  const [hasCheckedExisting, setHasCheckedExisting] = useState(false);
  
  const steps = [
    { id: 'prepare', title: 'Prepare Data', icon: <DocumentTextIcon className="w-5 h-5" /> },
    { id: 'generate', title: 'Generate 8-K', icon: <SparklesIcon className="w-5 h-5" /> },
    { id: 'press-release', title: 'Press Release', icon: <NewspaperIcon className="w-5 h-5" /> },
    { id: 'complete', title: 'Complete', icon: <CheckIconSolid className="w-5 h-5" /> }
  ];
  
  // Handle real-time WebSocket notifications for 8K generation
  const handle8KNotification = useCallback((notification: any) => {
    const { type, data } = notification;
    
    console.log('🔔 EightKGenerationModal received notification:', { type, data });
    
    switch (type) {
      case 'form8k_generation_started':
      case 'form8k_generation_regenerated':
        console.log('🚀 8K generation started notification');
        setRequestId(data.requestId);
        setGenerationStatus('generating');
        setCurrentStep(1);
        break;
        
      case 'form8k_generation_progress':
        console.log('📈 8K progress update:', { 
          percentage: data.percentage, 
          stage: data.stage, 
          message: data.message 
        });
        setProgress(data.percentage || 0);
        setProgressStage(data.stage || 'queued');
        setProgressMessage(data.message || '');
        break;
        
      case 'form8k_generation_completed':
        console.log('✅ 8K generation completed notification');
        setGenerationStatus('completed');
        setProgress(100);
        setProgressStage('completed');
        setCurrentStep(2);
        // Fetch download links
        if (data.requestId) {
          fetch8KCompletedGeneration(data.requestId);
        }
        break;
        
      case 'form8k_generation_error':
        console.log('❌ 8K generation error notification');
        setGenerationStatus('error');
        setErrorMessage(data.message || 'Generation failed');
        break;
    }
  }, []);
  
  // Process WebSocket notifications
  useEffect(() => {
    const form8kNotifications = notifications.filter((n: any) => 
      n.type.startsWith('form8k_generation')
    );
    
    console.log('🔍 EightKGenerationModal - Filtered 8K notifications:', form8kNotifications.length);
    
    // Process the most recent unread notification
    const latestNotification = form8kNotifications.find((n: any) => !n.read);
    if (latestNotification) {
      console.log('📨 Processing latest 8K notification:', latestNotification);
      handle8KNotification(latestNotification);
    }
  }, [notifications, handle8KNotification]);

  // Check for existing 8K generation on component mount
  useEffect(() => {
    if (!hasCheckedExisting && facilityId) {
      console.log('🔄 EightKGenerationModal mounted, checking existing generation...');
      checkExisting8KGeneration();
    }
  }, [facilityId, hasCheckedExisting]);
  
  // Check for existing 8K generation status
  const checkExisting8KGeneration = async () => {
    try {
      setHasCheckedExisting(true);
      console.log('🔍 Checking existing 8K generation for facility:', facilityId);
      const response = await axios.get(
        `${API_URL}/api/8k-generation/facilities/${facilityId}/status`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('📊 8K Generation API Response:', response.data);
      
      if (response.data.success && response.data.status) {
        const activeGeneration = ['pending', 'running'].includes(response.data.status);
        const completedGeneration = response.data.status === 'completed';
        
        console.log('📋 8K Generation Analysis:', {
          status: response.data.status,
          activeGeneration,
          completedGeneration,
          hasDownloadLinks: !!response.data.downloadLinks
        });
        
        if (activeGeneration) {
          console.log('⚡ Found active 8K generation, setting up real-time tracking...');
          setRequestId(response.data.requestId);
          setPythonTaskId(response.data.pythonTaskId);
          setGenerationStatus('generating');
          setCurrentStep(1);
          setProgress(response.data.progressPercentage || 0);
          setProgressStage(response.data.progressStage || 'queued');
          setProgressMessage(response.data.progressMessage || 'Processing...');
        } else if (completedGeneration) {
          console.log('✅ Found completed 8K generation');
          setExistingGeneration(response.data);
          setRequestId(response.data.requestId);
          setGenerationStatus('completed');
          setFiles(response.data.filePaths);
          setDownloadLinks(response.data.downloadLinks);
          setCurrentStep(2);
        }
      } else {
        console.log('ℹ️ No existing 8K generation found for facility');
      }
    } catch (error: any) {
      console.error('❌ Error checking existing 8K generation:', error);
      if (error.response?.status === 404) {
        console.log('✅ No existing 8K generation found - starting fresh');
      }
    }
  };
  
  // Fetch completed 8K generation details
  const fetch8KCompletedGeneration = async (requestId: string) => {
    try {
      console.log('📥 Fetching completed 8K generation details for:', requestId);
      const response = await axios.get(
        `${API_URL}/api/8k-generation/facilities/${facilityId}/status`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        setFiles(response.data.filePaths);
        setDownloadLinks(response.data.downloadLinks);
        console.log('📁 8K files available:', response.data.filePaths);
        console.log('📁 8K download links set:', response.data.downloadLinks);
      }
    } catch (error) {
      console.error('Error fetching completed 8K generation:', error);
    }
  };
  
  const handleStartGeneration = async () => {
    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }
    
    if (currentStep !== 1 || loading) return;
    
    setLoading(true);
    setGenerationStatus('generating');
    setProgress(0);
    
    try {
      // Start 8K generation using Python FastAPI integration
      const response = await axios.post(
        `${API_URL}/api/8k-generation/facilities/${facilityId}/generate`,
        {
          useParallel: true,
          companyName: facilityData?.companyName || 'Company',
          facilityData: facilityData
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        const requestId = response.data.requestId;
        setRequestId(requestId);
        // No polling needed - WebSocket notifications handle progress
      } else {
        throw new Error(response.data.message || 'Failed to start generation');
      }
    } catch (error: any) {
      console.error('Error starting 8-K generation:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to start 8-K generation');
      setGenerationStatus('error');
    } finally {
      setLoading(false);
    }
  };

        

  const handleDownloadFile = async (fileType: string) => {
    if (!requestId) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/8k-generation/${requestId}/download/${fileType}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `8K_${facilityData?.ticker || 'Document'}_${fileType}.${fileType === 'markdown' ? 'md' : fileType}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        alert('Files are no longer available. Please regenerate the 8K document to create new files.');
      } else {
        alert('Failed to download file. Please try again.');
      }
    }
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'pending';
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primaryDark }}>
          Generate Form 8-K
        </h3>
        <p className="text-gray-600 mb-6">
          AI-powered generation of SEC Form 8-K using template-based adaptation from real filings.
        </p>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center flex-1">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                getStepStatus(index) === 'completed' 
                  ? 'bg-green-500 text-white' 
                  : getStepStatus(index) === 'current'
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {step.icon}
              </div>
              <span className={`text-sm font-medium ${
                getStepStatus(index) === 'current' ? 'text-orange-600' : 'text-gray-500'
              }`}>
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div className="absolute top-6 left-1/2 w-full h-0.5 bg-gray-200 -z-10" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      {currentStep === 0 && (
        <div className="text-center">
          <DocumentTextIcon className="w-16 h-16 mx-auto mb-4" style={{ color: colors.primary }} />
          <h4 className="text-xl font-semibold mb-4" style={{ color: colors.primaryDark }}>
            Ready to Generate 8-K
          </h4>
          <p className="text-gray-600 mb-6">
            We'll use your facility terms and company information to generate a professional SEC Form 8-K.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">What will be generated:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Complete SEC Form 8-K current report</li>
                  <li>Professional press release</li>
                  <li>Both documents as downloadable PDFs</li>
                </ul>
              </div>
            </div>
          </div>
          <button
            onClick={handleStartGeneration}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
          >
            Continue to Generation
          </button>
        </div>
      )}

      {currentStep === 1 && (
        <div className="text-center">
          <SparklesIcon className="w-16 h-16 mx-auto mb-4" style={{ color: colors.accent }} />
          <h4 className="text-xl font-semibold mb-4" style={{ color: colors.primaryDark }}>
            Generate Your 8-K Filing
          </h4>
          
          {generationStatus === 'idle' && (
            <>
              <p className="text-gray-600 mb-6">
                Ready to generate your SEC Form 8-K using AI. This typically takes 3-5 minutes.
              </p>
              <button
                onClick={handleStartGeneration}
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                {loading ? 'Starting...' : 'Generate 8-K Now'}
              </button>
            </>
          )}
          
          {generationStatus === 'generating' && (
            <>
              <p className="text-gray-600 mb-6">
                Generating your 8-K filing using template-based adaptation...
              </p>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div 
                  className="bg-orange-500 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mb-4">{progress}% Complete</p>
              <div className="flex items-center justify-center">
                <ArrowPathIcon className="w-5 h-5 animate-spin text-orange-500 mr-2" />
                <span className="text-orange-600 font-medium">Processing...</span>
              </div>
            </>
          )}
          
          {generationStatus === 'error' && (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3" />
                  <span className="text-red-800 font-medium">Generation Failed</span>
                </div>
                {errorMessage && (
                  <p className="text-red-700 mt-2">{errorMessage}</p>
                )}
              </div>
              <button
                onClick={() => {
                  setGenerationStatus('idle');
                  setErrorMessage(null);
                  setProgress(0);
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      )}

      {currentStep === 2 && generationStatus === 'completed' && (
        <div className="text-center">
          <CheckIconSolid className="w-16 h-16 mx-auto mb-4" style={{ color: colors.success }} />
          <h4 className="text-xl font-semibold mb-4" style={{ color: colors.primaryDark }}>
            8-K Generated Successfully!
          </h4>
          <p className="text-gray-600 mb-6">
            Your SEC Form 8-K and press release have been generated and are ready for download.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <DocumentTextIcon className="w-8 h-8 mx-auto mb-2" style={{ color: colors.primary }} />
              <h5 className="font-semibold mb-2">Form 8-K Draft</h5>
              <p className="text-sm text-gray-600 mb-3">
                Complete SEC current report ready for filing
              </p>
              <button
                onClick={() => handleDownloadFile('docx')}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded font-medium transition-colors"
                disabled={!downloadLinks?.docx || !files?.docx}
              >
                Download 8-K DOCX
              </button>
            </div>
            
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <NewspaperIcon className="w-8 h-8 mx-auto mb-2" style={{ color: colors.accent }} />
              <h5 className="font-semibold mb-2">Press Release</h5>
              <p className="text-sm text-gray-600 mb-3">
                Professional announcement for public disclosure
              </p>
              <button
                onClick={() => handleDownloadFile('docx')}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded font-medium transition-colors"
                disabled={!downloadLinks?.docx}
              >
                Download 8-K DOCX
              </button>
            </div> */}
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center">
              <CheckIconSolid className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-800 font-medium">Task Completed Successfully</span>
            </div>
          </div>
          
          <button
            onClick={() => {
              onComplete();
              onClose();
            }}
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
          >
            Mark as Complete
          </button>
        </div>
      )}
    </div>
  );
};

// Generic Task Modal for other checklist items
const GenericTaskModal: React.FC<ModalContentProps> = ({ item, facilityId, onClose, onComplete, facilityData, onRefreshChecklist }) => {
  const [loading, setLoading] = useState(false);
  
  const handleTaskAction = async () => {
    setLoading(true);
    try {
      // Simulate task execution
      await new Promise(resolve => setTimeout(resolve, 2000));
      onComplete();
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Failed to complete task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-3" style={{ color: colors.primaryDark }}>
          {item.title}
        </h2>
        <p className="text-gray-600 text-lg">{item.description}</p>
      </div>

      {/* Task Information */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-8 shadow-inner">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h3 className="font-bold text-xl mb-6" style={{ color: colors.primaryDark }}>
            Task Details:
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <ClockIcon className="w-5 h-5 text-gray-500 mr-3" />
              <span className="text-gray-700">Estimated Time: {item.estimatedTime}</span>
            </div>
            
            {item.requiredDocs && (
              <div className="flex items-start">
                <DocumentDuplicateIcon className="w-5 h-5 text-gray-500 mr-3 mt-0.5" />
                <div>
                  <span className="text-gray-700">Required Documents:</span>
                  <ul className="list-disc list-inside ml-4 mt-2">
                    {item.requiredDocs.map((doc, index) => (
                      <li key={index} className="text-gray-600">{doc}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            
            {item.warningMessage && (
              <div className="mt-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{item.warningMessage}</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              This task is currently in development. Click below to mark as completed for testing purposes.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onClose}
          className="px-6 py-3 text-gray-700 hover:text-gray-900 font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleTaskAction}
          disabled={loading}
          className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50"
          style={{
            background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
          }}
        >
          {loading ? 'Processing...' : 'Complete Task'}
        </button>
      </div>
    </div>
  );
};

// Main Redesigned Checklist Component
const PreClosingChecklistRedesigned: React.FC = () => {
  const navigate = useNavigate();
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [facilityData, setFacilityData] = useState<any>(null);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  // const [isLoading, setIsLoading] = useState(true); // Unused - removed
    const { setCompletionPercentage } = useChecklist(); 
  
  const fetchFacilityData = useCallback(async (id: string) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/dashboard/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        setFacilityData(response.data.data);
        // After getting facility data, check signing status
        await checkSigningStatus(id);
      }
    } catch (error) {
      console.error('Error fetching facility data:', error);
    }
  }, []);

  useEffect(() => {
    // Initialize facility data
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      if (user.facilityId) {
        setFacilityId(user.facilityId);
        fetchFacilityData(user.facilityId);
      }
    }
    
    // Initialize checklist items
    initializeChecklist();
  }, [fetchFacilityData]);

  const checkSigningStatus = async (facilityId: string) => {
    try {
      const statusResponse = await axios.get(
        `${API_URL}/api/pre-closing/signing-status/${facilityId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (statusResponse.data.success) {
        const signingStatus = statusResponse.data;
        console.log('📄 Checking signing status on page load:', signingStatus);
        
        // Update the deal-docs checklist item if signing is completed
        if (signingStatus.localStatus === 'completed') {
          console.log('✅ Document is signed, updating checklist item');
          setChecklistItems(prev =>
            prev.map(item =>
              item.id === 'deal-docs' ? { ...item, status: 'completed' } : item
            )
          );
          // Also save to localStorage for persistence
          localStorage.setItem(`checklist_deal-docs_${facilityId}`, 'completed');
        }
      }
    } catch (error) {
      console.error('Error checking signing status:', error);
    }
  };

  const initializeChecklist = () => {
    const items: ChecklistItem[] = [
      {
        id: 'deal-docs',
        number: 1,
        title: 'Review and Sign Master Deal Documents',
        description: 'Review the updated deal document template and sign all required agreements',
        icon: <DocumentTextIcon className="h-6 w-6" />,
        status: 'pending',
        estimatedTime: '15-20 minutes',
        requiredDocs: ['Master Facility Agreement', 'Registration Rights Agreement', 'Secretary Certificate'],
        action: () => setActiveModal('deal-docs')
      },
      {
        id: 's1-filing',
        number: 2,
        title: 'Generate and Review Draft S-1/F-1 Registration Statement',
        description: 'AI-powered generation of SEC registration documents with legal review letter',
        icon: <DocumentArrowUpIcon className="h-6 w-6" />,
        status: 'pending',
        estimatedTime: '5-10 minutes to generate',
        requiredDocs: ['Recent Company S-1/F-1', 'Facility Terms'],
        action: () => setActiveModal('s1-filing')
      },
      {
        id: 'form-8k',
        number: 3,
        title: 'Generate Draft Form 8-K',
        description: 'AI-powered generation of Form 8-K for announcing the equity facility',
        icon: <NewspaperIcon className="h-6 w-6" />,
        status: 'pending',
        estimatedTime: '3-5 minutes',
        requiredDocs: ['Recent Company 8-K', 'Transaction Terms'],
        action: () => setActiveModal('form-8k')
      },
      {
        id: 'transfer-agent',
        number: 4,
        title: 'Provide Transfer Agent Details',
        description: 'Submit transfer agent contact information and generate document package',
        icon: <UserGroupIcon className="h-6 w-6" />,
        status: 'pending',
        estimatedTime: '5 minutes',
        warningMessage: 'Required before facility activation',
        action: () => setActiveModal('transfer-agent')
      }
    ];
    
    // Restore completion status from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      if (user.facilityId) {
        items.forEach(item => {
          const savedStatus = localStorage.getItem(`checklist_${item.id}_${user.facilityId}`);
          console.log(`🔍 Checking localStorage for ${item.id}: key=checklist_${item.id}_${user.facilityId}, value=${savedStatus}`);
          if (savedStatus === 'completed') {
            item.status = 'completed';
            console.log(`📋 Restored ${item.id} status from localStorage: completed`);
          } else {
            console.log(`📋 ${item.id} status remains: ${item.status}`);
          }
        });
      }
    }
    
    setChecklistItems(items);
    // setIsLoading(false); // Removed - isLoading state no longer used
  };

  const handleModalClose = () => {
    setActiveModal(null);
  };

  const handleItemComplete = (itemId: string) => {
    setChecklistItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, status: 'completed' } : item
      )
    );
    setActiveModal(null);
    
    // Save completion status
    localStorage.setItem(`checklist_${itemId}_${facilityId}`, 'completed');
  };

  const completedCount = checklistItems.filter(item => item.status === 'completed').length;
  const completionPercentage = checklistItems.length > 0 ? (completedCount / checklistItems.length) * 100 : 0;

    useEffect(() => {
    console.log("completion percentage changed:", completionPercentage);
    setCompletionPercentage(completionPercentage);
  }, [completionPercentage, setCompletionPercentage]);
  

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <LightNavigation 
        title="Pre-Closing Checklist"
        showBackButton={true}
        onBack={() => navigate('/dashboard')}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Card */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl p-8 mb-8 relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute top-0 right-0 w-96 h-96 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `repeating-linear-gradient(45deg, ${colors.primary} 0, ${colors.primary} 2px, transparent 2px, transparent 10px)`,
            }} />
          </div>
          
          {/* Content */}
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold mb-2" style={{ color: colors.primaryDark }}>
                  Pre-Closing Checklist
                </h1>
                <p className="text-gray-600 text-lg">
                  Complete these final steps to activate your equity financing facility
                </p>
              </div>
              <div className="text-center">
                <div className="relative w-32 h-32">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke={colors.gray[200]}
                      strokeWidth="12"
                      fill="none"
                    />
                    <motion.circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke={colors.accent}
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={351.86}
                      initial={{ strokeDashoffset: 351.86 }}
                      animate={{ strokeDashoffset: 351.86 - (351.86 * completionPercentage / 100) }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div>
                      <p className="text-3xl font-bold" style={{ color: colors.primaryDark }}>
                        {completionPercentage.toFixed(0)}%
                      </p>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Complete</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium" style={{ color: colors.gray[700] }}>
                  Overall Progress
                </span>
                <span className="text-sm font-medium" style={{ color: colors.primaryDark }}>
                  {completedCount} of {checklistItems.length} tasks completed
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${completionPercentage}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  style={{
                    background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight})`
                  }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Checklist Items */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold mb-8" style={{ color: colors.primaryDark }}>
            Tasks to Complete
          </h2>
          
          <div className="space-y-6">
            {checklistItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative group cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
                  item.status === 'completed' ? 'opacity-90' : ''
                }`}
                onClick={item.action}
              >
                <div className={`flex items-start p-6 rounded-2xl border-2 transition-all duration-300 ${
                  item.status === 'completed' 
                    ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-lg'
                    : 'bg-white border-gray-200 hover:border-orange-300 hover:shadow-lg'
                }`}>
                  {/* Checkbox Circle */}
                  <div className="flex-shrink-0 mr-6">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-12 h-12 rounded-full border-3 flex items-center justify-center transition-all duration-300 ${
                        item.status === 'completed'
                          ? 'border-green-500 bg-green-500'
                          : 'border-gray-300 bg-white group-hover:border-orange-400'
                      }`}
                    >
                      {item.status === 'completed' ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        >
                          <CheckIconSolid className="w-6 h-6 text-white" />
                        </motion.div>
                      ) : (
                        <span className="text-lg font-bold" style={{ color: colors.gray[400] }}>
                          {item.number}
                        </span>
                      )}
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className={`text-lg font-semibold mb-2 ${
                          item.status === 'completed' ? 'text-green-700' : ''
                        }`} style={{ color: item.status === 'completed' ? '' : colors.primaryDark }}>
                          {item.title}
                        </h3>
                        <p className={`text-sm mb-3 ${
                          item.status === 'completed' ? 'text-green-600' : 'text-gray-600'
                        }`}>
                          {item.description}
                        </p>
                        
                        {/* Meta Information */}
                        <div className="flex items-center space-x-6 text-xs">
                          {item.estimatedTime && (
                            <div className="flex items-center text-gray-500">
                              <ClockIcon className="w-4 h-4 mr-1" />
                              <span>{item.estimatedTime}</span>
                            </div>
                          )}
                          {item.requiredDocs && (
                            <div className="flex items-center text-gray-500">
                              <DocumentDuplicateIcon className="w-4 h-4 mr-1" />
                              <span>{item.requiredDocs.length} documents</span>
                            </div>
                          )}
                          {item.warningMessage && item.status !== 'completed' && (
                            <div className="flex items-center text-amber-600">
                              <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                              <span>{item.warningMessage}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Button/Status */}
                      <div className="ml-6">
                        {item.status === 'completed' ? (
                          <div className="flex flex-col items-end">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer transition-colors">
                              Completed - Click to Review
                            </span>
                          </div>
                        ) : (
                          <button
                            className="px-6 py-2 rounded-xl text-white font-medium shadow-md hover:shadow-lg transition-all transform hover:scale-105"
                            style={{
                              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              item.action?.();
                            }}
                          >
                            Start
                            <ChevronRightIcon className="w-4 h-4 inline-block ml-2" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connecting Line */}
                {index < checklistItems.length - 1 && (
                  <div className="absolute left-9 top-24 bottom-0 w-0.5 bg-gray-200" />
                )}
              </motion.div>
            ))}
          </div>

          {/* Completion Message */}
          {completionPercentage === 100 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-12 p-8 rounded-2xl text-center"
              style={{
                background: `linear-gradient(135deg, ${colors.success}20, ${colors.successLight}20)`
              }}
            >
              <SparklesIcon className="w-16 h-16 mx-auto mb-4" style={{ color: colors.success }} />
              <h3 className="text-2xl font-bold mb-3" style={{ color: colors.primaryDark }}>
                All Tasks Completed!
              </h3>
              <p className="text-gray-600 mb-6">
                Your pre-closing checklist is complete. Your facility will be activated upon S-1 effectiveness.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})`
                }}
              >
                Return to Dashboard
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Modal Overlay */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex justify-between items-center rounded-t-3xl">
                <div className="flex items-center">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mr-4"
                    style={{ 
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})` 
                    }}
                  >
                    {checklistItems.find(i => i.id === activeModal)?.icon}
                  </div>
                  <h2 className="text-2xl font-bold" style={{ color: colors.primaryDark }}>
                    Task Details
                  </h2>
                </div>
                <button
                  onClick={handleModalClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-7 w-7" />
                </button>
              </div>
              
              {activeModal === 'deal-docs' && (
                <DealDocumentsModal
                  item={checklistItems.find(i => i.id === 'deal-docs')!}
                  facilityId={facilityId!}
                  onClose={handleModalClose}
                  onComplete={() => handleItemComplete('deal-docs')}
                  facilityData={facilityData}
                />
              )}
              
              {activeModal === 's1-filing' && (
                <S1GenerationModal
                  item={checklistItems.find(i => i.id === 's1-filing')!}
                  facilityId={facilityId!}
                  onClose={handleModalClose}
                  onComplete={() => handleItemComplete('s1-filing')}
                  facilityData={facilityData}
                  onRefreshChecklist={initializeChecklist}
                />
              )}
              
              {activeModal === 'form-8k' && (
                <EightKGenerationModal
                  item={checklistItems.find(i => i.id === 'form-8k')!}
                  facilityId={facilityId!}
                  onClose={handleModalClose}
                  onComplete={() => handleItemComplete('form-8k')}
                  facilityData={facilityData}
                />
              )}
              
              {activeModal === 'transfer-agent' && (
                <GenericTaskModal
                  item={checklistItems.find(i => i.id === 'transfer-agent')!}
                  facilityId={facilityId!}
                  onClose={handleModalClose}
                  onComplete={() => handleItemComplete('transfer-agent')}
                  facilityData={facilityData}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PreClosingChecklistRedesigned;